import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import ParcelMap, { plotSize } from '../../components/ParcelMap.jsx';
import PlanChecks from '../../components/PlanChecks.jsx';
import { EmptyState, ErrorState, Field, Modal, Spinner } from '../../components/ui.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, timeAgo } from '../../lib/format.js';

/** A verdict on a plot or a layout, or how a dispute was settled, with a note. */
const VerdictModal = ({ title, lead, label, confirmLabel, tone, requireNote, onSubmit, onClose }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await onSubmit(note.trim() || null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="verdict-form" className={`btn btn-${tone}`} disabled={busy || (requireNote && note.trim().length < 3)}>
            {busy ? 'Saving...' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="modal-lead">{lead}</p>
      <form id="verdict-form" onSubmit={submit}>
        <Field label={label}>
          <textarea rows="3" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </form>
    </Modal>
  );
};

const PlanLink = ({ url, children }) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
      {children}
    </a>
  ) : (
    <span className="overdue-flag">{children}: not uploaded</span>
  );

/**
 * Checking the land register. Every plot for sale passes through here: the
 * chief-signed site plan, and a layout's general plan, compared with the
 * boundary the owner drew, before anybody can be offered the land.
 */
const AdminLand = () => {
  useTitle('Land register checks');
  const toast = useToast();
  const queue = useFetch('/admin/land');
  const [verdict, setVerdict] = useState(null);

  if (queue.loading && !queue.data) return <Spinner />;
  if (queue.error) {
    return (
      <div className="container section">
        <ErrorState message={queue.error} onRetry={queue.reload} />
      </div>
    );
  }
  const { parcels, layouts, disputes } = queue.data;

  const decide = async (note) => {
    try {
      if (verdict.kind === 'dispute') {
        await api.patch(`/admin/land/disputes/${verdict.item.id}`, { note });
      } else {
        await api.patch(`/admin/land/${verdict.kind === 'layout' ? 'layouts' : 'parcels'}/${verdict.item.id}`, { status: verdict.status, note });
      }
      toast.success('Recorded.');
      setVerdict(null);
      queue.reload();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Land register checks</h1>
          <p className="muted">
            Compare each chief-signed site plan with the boundary drawn on the map. Check a layout's general plan before
            the plots in it.
          </p>
        </div>
        <Link to="/land" className="btn btn-ghost">
          Public register
        </Link>
      </header>

      <section className="section-block">
        <div className="section-head">
          <h2>Open conflicts</h2>
          <span className="muted">{disputes.length} open. The plot's sale is paused while each is open.</span>
        </div>
        {disputes.length === 0 ? (
          <EmptyState title="No open conflicts" />
        ) : (
          <div className="stack">
            {disputes.map((dispute) => (
              <article key={dispute.id} className="panel review-card">
                <div className="review-card-head">
                  <strong>
                    <Link to={`/land/${dispute.parcelId}`}>{dispute.reference}</Link>, {dispute.village}
                  </strong>
                  <span className="muted">{timeAgo(dispute.createdAt)}</span>
                </div>
                <p>"{dispute.reason}"</p>
                <p className="muted">
                  Reported by {dispute.reporter?.name || 'a removed account'}
                  {dispute.reporter?.phone ? `, ${dispute.reporter.phone}` : ''}
                  {dispute.reporter?.email ? `, ${dispute.reporter.email}` : ''}
                </p>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setVerdict({ kind: 'dispute', item: dispute })}>
                  Record it settled
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="section-block">
        <div className="section-head">
          <h2>Layouts to check</h2>
          <span className="muted">{layouts.length} waiting</span>
        </div>
        {layouts.length === 0 ? (
          <EmptyState title="No layouts waiting" />
        ) : (
          <div className="stack">
            {layouts.map((layout) => (
              <article key={layout.id} className="panel review-card">
                <div className="review-card-head">
                  <strong>
                    {layout.name}, {layout.village}
                  </strong>
                  <span className="muted">General plan signed by {layout.chiefName}</span>
                </div>
                <div className="row-actions">
                  <PlanLink url={layout.generalPlanUrl}>General plan</PlanLink>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!layout.generalPlanUrl}
                    onClick={() => setVerdict({ kind: 'layout', status: 'verified', item: layout })}
                  >
                    Verify
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setVerdict({ kind: 'layout', status: 'rejected', item: layout })}>
                    Turn down
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="section-block">
        <div className="section-head">
          <h2>Plots to check</h2>
          <span className="muted">{parcels.length} waiting</span>
        </div>
        {parcels.length === 0 ? (
          <EmptyState title="No plots waiting" message="New registrations land here once they are submitted." />
        ) : (
          <div className="stack">
            {parcels.map((plot) => (
              <article key={plot.id} className="panel review-card">
                <div className="review-card-head">
                  <strong>
                    {plot.reference} - {plot.village}
                    {plot.plotNumber ? `, plot ${plot.plotNumber}` : ''}
                    {plot.layout ? ` in ${plot.layout.name}` : ''}
                  </strong>
                  <span className="muted">{plotSize(plot.areaSqm)}</span>
                </div>
                <div className="grid-2">
                  <ParcelMap
                    height={260}
                    plots={[{ id: plot.id, boundary: plot.boundary, tone: 'plot', label: plot.reference }]}
                  />
                  <dl className="plot-facts">
                    <div>
                      <dt>Owner</dt>
                      <dd>
                        {plot.owner?.name}
                        {plot.owner?.phone ? `, ${plot.owner.phone}` : ''}
                      </dd>
                    </div>
                    <div>
                      <dt>Chief</dt>
                      <dd>
                        {plot.chiefName}
                        {plot.chiefSignedOn ? `, signed ${formatDate(plot.chiefSignedOn)}` : ''}
                      </dd>
                    </div>
                    <div>
                      <dt>Certificate</dt>
                      <dd>{plot.certificateNumber || 'None given'}</dd>
                    </div>
                    <div>
                      <dt>Touches</dt>
                      <dd>{plot.neighbours.length ? plot.neighbours.map((other) => `PLOT-${String(other.id).padStart(5, '0')} (${other.sharedSqm} m2)`).join(', ') : 'No registered plot'}</dd>
                    </div>
                  </dl>
                </div>
                {plot.planAnalysis ? (
                  <PlanChecks analysis={plot.planAnalysis} title="Read automatically from the site plan" />
                ) : plot.sitePlanUrl ? (
                  <p className="muted">The site plan has not been read automatically - compare it by eye.</p>
                ) : null}
                <div className="row-actions">
                  <PlanLink url={plot.sitePlanUrl}>Chief-signed site plan</PlanLink>
                  {plot.layout ? <PlanLink url={plot.generalPlanUrl}>Layout general plan</PlanLink> : null}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!plot.sitePlanUrl}
                    onClick={() => setVerdict({ kind: 'parcel', status: 'verified', item: plot })}
                  >
                    Verify
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setVerdict({ kind: 'parcel', status: 'rejected', item: plot })}>
                    Turn down
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {verdict ? (
        <VerdictModal
          title={
            verdict.kind === 'dispute'
              ? `Settle the conflict on ${verdict.item.reference}`
              : verdict.status === 'verified'
                ? `Verify ${verdict.item.reference || verdict.item.name}?`
                : `Turn down ${verdict.item.reference || verdict.item.name}?`
          }
          lead={
            verdict.kind === 'dispute'
              ? 'Say how it was settled. The plot goes back on sale if nothing else holds it.'
              : verdict.status === 'verified'
                ? 'Only if the plan is signed and stamped by the chief and the boundary drawn matches it.'
                : 'The owner sees your reason and can put it right.'
          }
          label={verdict.kind === 'dispute' ? 'How it was settled' : verdict.status === 'verified' ? 'Note (optional)' : 'What is wrong'}
          confirmLabel={verdict.kind === 'dispute' ? 'Record it settled' : verdict.status === 'verified' ? 'Verify' : 'Turn it down'}
          tone={verdict.status === 'rejected' ? 'danger' : 'primary'}
          requireNote={verdict.kind === 'dispute' || verdict.status === 'rejected'}
          onSubmit={decide}
          onClose={() => setVerdict(null)}
        />
      ) : null}
    </div>
  );
};

export default AdminLand;
